import angular from 'angular';
import addEditProcessorTemplate from './addEditProcessor.html';
import './addEditProcessor.scss';

/*******************************************************************************
 * @ngdoc    overview
 * @module   csm.books.OurOrg.AddEditProcessor
 * @description
 * Dialog allowing the entry/edit of payment processors. Dialog template is
 * `addEditProcessorComponent` and can be modified by the loading application
 * with transcludes to take input of information specific to the app.
 *******************************************************************************/

/*******************************************************************************
 * AddEditProcessorDialog */
/**
 * @ngdoc    provider
 * @module   csm.books.OurOrg
 * @name     csm.books.OurOrg.provider:AddEditProcessorDialogProvider
 * @property {string}  template     Dialog template content as a string.
 * @description
 * Provider for configuring the Add/Edit Processor modal dialog. The `template`
 * property can be overridden by consuming applications (like ASBWorks) to provide
 * custom templates with transcluded content for specialized functionality such as
 * ASB GASB codes.
 *******************************************************************************/
/* @ngInject */ export function AddEditProcessorDialogProvider() {
  var $prov = this;

  /**
   * @ngdoc    service
   * @module   csm.books.OurOrg
   * @name     csm.books.OurOrg.service:AddEditProcessorDialog
   * @description
   * Service that creates and displays the Add/Edit Processor modal dialog.
   * Uses the configured template string to support both default and custom
   * implementations with transcluded content.
   */
  this.$get = [
    '$uibModal',
    function ($uibModal) {
      /**
       * @ngdoc    method
       * @name     csm.books.OurOrg.service:AddEditProcessorDialog#show
       * @param   {object[][]} accounts   Filtered account lists for account selection.
       * @param   {object}     processor  Processor info to be modified, null=add new processor.
       * @param   {object[]}   processors List of currently defined processors.
       * @param   {boolean}    kpsActive  Whether KPS processor is active.
       * @param   {boolean}    cpKpsActive  Whether CP KPS processor is active.
       * @returns {object}     `$uibModal.open().result` promise.
       * @description
       * Opens the Add/Edit Processor modal dialog with the configured template.
       * ASBWorks has certain transcluded content that is added onto the existing template.
       */
      return {
        show: function (
          accounts,
          processor,
          processors,
          kpsActive,
          cpKpsActive
        ) {
          return $uibModal.open({
            template: $prov.template,
            animation: true,
            backdrop: 'static',
            size: 'lg',
            controller: angular.noop,
            resolve: {
              accounts: function () {
                return accounts;
              },
              processor: function () {
                return processor;
              },
              processors: function () {
                return processors;
              },
              kpsActive: () => kpsActive,
              cpKpsActive: () => cpKpsActive
            }
          }).result;
        }
      };
    }
  ];
}

/*******************************************************************************
 * addEditProcessorDirective */
/**
 * @ngdoc   directive
 * @module  csm.books.OurOrg
 * @name    csm.books.OurOrg.component:addEditProcessorDirective
 * @slot    processingFeeInfo   Below Processing Fee expense account input.
 * @slot    convenienceFeeInfo  Below Convenience Fee input account input.
 * @slot    recordFeeInfo       Below Record Fee expense account input.
 * @description
 * Dialog template component. Transclusion slots modify the account input to
 * allow for ASB GASB codes.
 *******************************************************************************/
/* @ngInject */ export function addEditProcessorDirective() {
  return {
    template: addEditProcessorTemplate,
    controller: controller,
    controllerAs: '$ctrl',
    transclude: {
      processingFeeInfo: '?processingFeeInfo',
      convenienceFeeInfo: '?convenienceFeeInfo',
      recordFeeInfo: '?recordFeeInfo'
    }
  };
}
addEditProcessorDirective.$inject = [];

controller.$inject = [
  '$element',
  '$injector',
  '$scope',
  '$transclude',
  'AppConfig',
  'NewBooksData',
  'CompilerService',
  'SERVER',
  '$document'
];

/*******************************************************************************
 * Controller
 *******************************************************************************/
/* @ngInject */ function controller(
  $element,
  $injector,
  $scope,
  $transclude,
  AppConfig,
  NewBooksData,
  CompilerService,
  SERVER,
  $document
) {
  var $svcs = { $ctrl: this, $scope: $scope };
  var $ctrl = Object.defineProperties(
    Object.assign(this, {
      /*-------*/
      /* Input */
      /*-------*/
      processors: $scope.$resolve.processors, // Current list of Processors.
      bankAccts: $scope.$resolve.accounts.bankAccts, // Merchant accounts data provider.
      procExpAccts: $scope.$resolve.accounts.procExpAccts, // Processing Fee expense accounts data provider.
      feeIncAccts: $scope.$resolve.accounts.feeIncAccts, // Convenience Fee income accounts data provider.
      noSaleAccts: $scope.$resolve.accounts.noSaleAccts, // "No Sale" Income accounts data provider.
      undepAccts: $scope.$resolve.accounts.undepAccts, // Undeposited funds accounts data provider.

      /*-------------------------------------------*/
      /* Processor information. Copied from input. */
      /*-------------------------------------------*/
      pInfo: new NewBooksData.Processor(
        $scope.$resolve.processor || { active: true, apiTokens: {} }
      ),

      /*----------------------------------------*/
      /* Selected processor type configuration. */
      /* Changes when `pInfo.type` changes.     */
      /*----------------------------------------*/
      pConfig: {},

      /*-----------------------------------------------*/
      /* Default account name is set by tokens input   */
      /* component and replaces an empty account name. */
      /*-----------------------------------------------*/
      defaultName: null,

      /*-------*/
      /* Flags */
      /*-------*/
      isEdit: !!$scope.$resolve.processor, // Edit mode flag.
      kpsActive: $scope.$resolve.kpsActive, // KPS processor exists flag.
      cpKpsActive: $scope.$resolve.cpKpsActive, // CP KPS processor exists flag.
      isModified: false, // Set when `pInfo` is modified.
      isUntested: false, // Set when `pInfo.apiTokens` is modified. Cleared when tested.
      allowTestToggle: false, // Set when ctrl and shift keys are down to allow test account toggle.

      /*----------*/
      /* Handlers */
      /*----------*/
      cancel: $injector.invoke(cancel, null, $svcs), // Cancel button.
      onKeyEvent: $injector.invoke(onKeyEvent, null, $svcs), // Ctrl-shift key listener.
      save: $injector.invoke(save, null, $svcs), // Save button.
      testCredentials: $injector.invoke(testCredentials, null, $svcs), // Test credentials button.
      toggleTestAcct: $injector.invoke(toggleTestAcct, null, $svcs), // Test flag click handler.

      /*----------*/
      /* Watchers */
      /*----------*/
      unWatchActive: $injector.invoke(watchActive, null, $svcs), // Active flag.
      unWatchInfo: $injector.invoke(watchInfo, null, $svcs), // Processor info.
      unWatchTokens: $injector.invoke(watchTokens, null, $svcs), // API tokens.
      unWatchType: $injector.invoke(watchType, null, $svcs), // Processor type.

      /*--------------------------*/
      /* `csmValidators` objects. */
      /*--------------------------*/
      valAccounts: {}, // Accounts input.
      valTokens: {} // Tokens input.
    }),
    {
      /**
       * TODO: Eventually this can be removed once we no longer decide to show
       * the OpenEdge processor details by clicking on edit. Once we implement this
       * ticket, GR-2207, we can remove this property and any other OpenEdge checks.
       */
      isOpenEdgeProcessor: {
        get: () => $ctrl.pInfo.type === SERVER.BooksDBConstants.PT_OPEN_EDGE
      },
      /**
       * TODO: Eventually this can be removed once we no longer decide to show
       * the PACE processor details by clicking on edit. Once we implement this
       * ticket, GR-2207, we can remove this property and any other PACE checks.
       */
      isPaceProcessor: {
        get: () => $ctrl.pInfo.type === SERVER.BooksDBConstants.PT_PACE
      },
      /**
       * When its a KPS processor we do not show the Active flag radio buttons because we programatically control the active flag of the
       * processor. If its inactive then that means that kps has not been activated for the client yet. If its active then that means that
       * kps has been activated and the processor can NEVER be inactivated. This allows us to make it so we do not need to worry about users
       * accidentally setting a KPS processor to inactive or selecting the kps processor as the WS/CWS processor before the kps active flag
       * has been set through CSMAdmin.
       */
      isKpsProcessor: {
        get: () => $ctrl.pInfo.type === SERVER.BooksDBConstants.PT_KPS
      },
      /*---------------------*/
      /* Show element flags. */
      /*---------------------*/
      showFeeIncAcct: {
        get: () => $ctrl.pConfig.reqFeeInc && $ctrl.pInfo.forWS
      }, // Convenience Fee income account selector.
      showFeeExpAcct: {
        get: () => $ctrl.pConfig.reqProcExp && $ctrl.pInfo.forWS
      }, // Processing Fee expense account selector.
      showMerchAcct: { get: () => $ctrl.pConfig.reqMerch }, // Merchant account selector.
      showRecordAcct: { get: () => $ctrl.pConfig.reqNoSale }, // Record Fee income selector.
      showUndepAcct: { get: () => $ctrl.pConfig.reqUndep }, // Undeposited funds account selector.
      showTestBtn: {
        get: () =>
          $ctrl.pConfig.testTokens &&
          $ctrl.enableEdit &&
          !$ctrl.isPaceProcessor &&
          !$ctrl.isOpenEdgeProcessor
      }, // Test credentials button.
      showTestFlag: { get: () => $ctrl.pInfo.apiTokens.isTestAcct }, // Show test account indicator.
      /**
       * KPS Processors are both POS and Webstore processors so we do not show the
       * radio buttons to prevent confusion and incorrect save.
       */
      showPOS: {
        get: () =>
          $ctrl.pConfig.forPOS &&
          AppConfig.settings.usePOS &&
          !$ctrl.isKpsProcessor
      }, // Point-of-Sale checkbox.
      showWS: { get: () => $ctrl.pConfig.forWS && !$ctrl.isKpsProcessor }, // Webstore checkbox.
      showCreds: {
        get: () =>
          $ctrl.pInfo.type == SERVER.BooksDBConstants.PT_OPEN_EDGE || // Show credentials.
          $ctrl.pInfo.type == SERVER.BooksDBConstants.PT_PACE ||
          $ctrl.pInfo.type == SERVER.BooksDBConstants.PT_PAY_PAL ||
          $ctrl.pInfo.type == SERVER.BooksDBConstants.PT_PAY_PROS
      },
      /**
       * We only want to show the alert banner if the user is editing an existing processor
       * that is of a type that is no longer supported by the system.
       */
      showUnsupportedTypeMsg: {
        get: () =>
          $ctrl.isEdit && ($ctrl.isPaceProcessor || $ctrl.isOpenEdgeProcessor)
      },
      /*---------------------------*/
      /* Enable input field flags. */
      /*---------------------------*/
      enableEdit: {
        get: () => $ctrl.pConfig && ($ctrl.pInfo.forPOS || $ctrl.pInfo.forWS)
      }, // Show fields and enable editing.
      enableSave: { get: () => $ctrl.isModified && $ctrl.enableEdit }, // Save button.
      /**
       * If the processor is not active and a KPS processor exists then we know that the client has had KPS activated through
       * admin. Once a processor is activated the only WS processor that can exist is KPS.
       *
       * True = disable; False = enable
       */
      disableActivateProcessor: {
        get: () => !$ctrl.pInfo.active && $ctrl.kpsActive && $ctrl.pInfo.forWS
      },
      /**
       * When creating a new processor we need to make sure the For WS radio button is disabled if KPS is active for this client. Once
       * KPS has been set to active then only KPS processors are valid.
       */
      disableSetProcessorToWs: {
        get: () =>
          !enableFor() ||
          ($ctrl.kpsActive &&
            $ctrl.pInfo.type !== SERVER.BooksDBConstants.PT_KPS)
      },
      /**
       * When creating a new processor we need to make sure the For POS radio button is disabled if KPS Card Present (CP) is active
       * for this client. Once KPS CP has been set to active then only KPS processors are valid for POS.
       */
      disableSetProcessorToPos: {
        get: () =>
          !enableFor() ||
          ($ctrl.cpKpsActive &&
            $ctrl.pInfo.type !== SERVER.BooksDBConstants.PT_KPS)
      },
      /**
       * The merchant account should never be switched unless done by internal staff for KPS. This is because our KPS contracts are tied
       * to a specific bank account and if that changes then a new contract is written. We need to update the KPS merchant in the admin console
       * and if the processors bank account needs to be changed a internal staff member will need to do that.
       */
      disableMerchantAcct: {
        get: () =>
          $ctrl.pInfo.type === SERVER.BooksDBConstants.PT_KPS &&
          !SERVER.currentUser.impersonate
      },
      /*-----------------------*/
      /* POS/WS radio buttons. */
      /*-----------------------*/
      enableFor: $injector.invoke(enableFor, null, $svcs),
      forWhat: $injector.invoke(forWhat, null, $svcs)
    }
  );

  /*------------------------------------*/
  /* Build processor dropdown provider. */
  /*------------------------------------*/
  $ctrl.configs = SERVER.processorConfigs.filter((c) => {
    /*--------------------------------------------*/
    /* Processors in use must always be included. */
    /*--------------------------------------------*/
    if ($ctrl.pInfo.type === c.type) $ctrl.pConfig = c;
    // Ignore paypal if a kps processor exists since paypals can only be created for WS
    else if (c.type === SERVER.BooksDBConstants.PT_PAY_PAL && $ctrl.kpsActive)
      return false;
    /**
     * CSM Admin is the only place built for KPS processors to be created. So we never want to show the KPS processor
     * as an option to create brand new. They should only EVER be created in CSM Admin.
     */ else if (c.type === SERVER.BooksDBConstants.PT_KPS) return false;
    else if (!c.editable) return false;
    else if (c.type == SERVER.BooksDBConstants.PT_MANUAL) return true;
    /*---------------------------------------------------*/
    /* Include POS processors only if app allows POS.    */
    /* Currently Booster and PTA do not use POS so those */
    /* processors should not be available for them.      */
    /*---------------------------------------------------*/ else if (
      !AppConfig.settings.usePOS &&
      c.forPOS &&
      !c.forWS
    )
      return false;
    /**
     * PACE and OpenEdge processors are no longer supported by the system as of July 1st, 2026.
     * This should prevent new processors from being created but existing
     * processors will still show as its cover on the first check.
     */ else if (
      c.type === SERVER.BooksDBConstants.PT_PACE ||
      c.type === SERVER.BooksDBConstants.PT_OPEN_EDGE
    )
      return false;

    return true;
  });

  /*------------------------------------------------------------------*/
  /* Insert transcluded content and add reference to this controller. */
  /*------------------------------------------------------------------*/
  CompilerService.transcludeSlots($element, { $ctrl: $ctrl }, $transclude);

  /*--------------------------------------------------*/
  /* Add key up/down handler to watch for ctrl-click. */
  /*--------------------------------------------------*/
  $document.on('keyup keydown', $ctrl.onKeyEvent);
  $scope.$on('destroy', () =>
    $document.$off('keyup keydown', $ctrl.onKeyEvent)
  );
}

/*****************************************************************************
 * $ctrl.cancel */
/**
 * @function cancel
 * Cancel button handler. Resets validators and closes the dialog.
 *****************************************************************************/
/* @ngInject */ function cancel($ctrl, $scope) {
  return function () {
    $ctrl.valAccounts.reset();
    $ctrl.valTokens.reset();
    $scope.$dismiss();
  };
}

/*****************************************************************************
 * $ctrl.enableFor */
/**
 * @property boolean enableSource
 * Enable POS/Webstore checkboxes if both are allowed for the processor. If not,
 * Allowed source is set, automatically.
 *****************************************************************************/
/* @ngInject */ function enableFor($ctrl, $scope, SERVER) {
  return {
    get: function () {
      var enable = $ctrl.pConfig.forWS && $ctrl.pConfig.forPOS;

      /*-----------------------------------------------------------*/
      /* Pace config must disable webstore checkbox on edit        */
      /* if Webstore is already selected (ASB-386).                */
      /*                                                           */
      /* TODO: This is a temporary fix until we change over to not */
      /* TODO: allowing POS and Webstore on the same processor.    */
      /*-----------------------------------------------------------*/
      if ($ctrl.pConfig.type == SERVER.BooksDBConstants.PT_PACE && $ctrl.isEdit)
        enable &= !$scope.$resolve.processor.forWS;

      return enable;
    }
  };
}

/*****************************************************************************
 * $ctrl.forWhat */
/**
 * @property boolean forPOS
 * POS/WS radio button model. Toggles `forPOS` and `forWS`. Returns undefined
 * if both are false so user can select for new processor.
 *****************************************************************************/
/* @ngInject */ function forWhat($ctrl) {
  return {
    get: () =>
      $ctrl.pInfo.forPOS || $ctrl.pInfo.forWS ? $ctrl.pInfo.forPOS : undefined,
    set: (v) => ($ctrl.pInfo.forWS = !($ctrl.pInfo.forPOS = v))
  };
}

/*****************************************************************************
 * $ctrl.onKeyEvent */
/**
 * @property boolean onKeyEvent
 * Keyup/Keydown event handler. Sets flag to show the test indicator on hover
 * if ctrl and shift keys are down.
 *****************************************************************************/
/* @ngInject */ function onKeyEvent($ctrl, $scope) {
  return function (event) {
    if (
      !$ctrl.allowTestToggle &&
      event.type === 'keydown' &&
      event.ctrlKey &&
      event.shiftKey
    )
      $scope.$apply(() => ($ctrl.allowTestToggle = true));
    else if ($ctrl.allowTestToggle && event.type === 'keyup')
      $scope.$apply(() => ($ctrl.allowTestToggle = false));
  };
}

/*****************************************************************************
 * $ctrl.save */
/**
 * @ngdoc     function
 * @name      save
 * @methodOf  processors.controller.processorsController
 * @description
 * Validates all expected data has been set. If everything is present, close
 * the dialog and save the token.
 *****************************************************************************/
/* @ngInject */ function save(
  $ctrl,
  $scope,
  Alert,
  BusyScreen,
  Services,
  SERVER
) {
  return function () {
    /*-----------------*/
    /* Validate inputs */
    /*-----------------*/
    if (!($ctrl.valTokens.validate() && $ctrl.valAccounts.validate())) return;
    $ctrl.valAccounts.reset();
    $ctrl.valTokens.reset();

    try {
      /*-------------------------*/
      /* Validate processor use. */
      /*-------------------------*/
      //$ctrl.processors.filter(p => p.id != $ctrl.pInfo.id && p.type == $ctrl.pInfo.type).some(p =>
      //  {
      //  if (p.type == SERVER.BooksDBConstants.PT_MANUAL)
      //    throw 'Only one Manual setting allowed.';

      //  if (p.forPOS && $ctrl.pInfo.forPOS)
      //    throw `Only one ${$ctrl.pConfig.name} processor allowed for Point-Of-Sale`;
      //  });

      /*-----------------------*/
      /* Verify tokens tested. */
      /*-----------------------*/
      if ($ctrl.pInfo.active && $ctrl.pConfig.testTokens && $ctrl.isUntested)
        throw `Processor credentials must be tested`;
    } catch (message) {
      Alert.error(message, 'Not Ready to Save', true);
      return;
    }

    /*-------------------------------------*/
    /* Set the account name to the default */
    /* if current account name is empty.   */
    /*-------------------------------------*/
    if (!$ctrl.pInfo.accountName) $ctrl.pInfo.accountName = $ctrl.defaultName;

    /*--------------------------*/
    /* Clear unused properties. */
    /*--------------------------*/
    if (!$ctrl.pConfig.reqFeeInc) $ctrl.pInfo.feeIncomeAcctID = null;

    if (!$ctrl.pConfig.reqProcExp) $ctrl.pInfo.procExpenseAcctID = null;

    if (!$ctrl.pConfig.reqMerch) $ctrl.pInfo.merchantAcctID = null;

    if (!$ctrl.pConfig.reqUndep) $ctrl.pInfo.undepFundsAcctID = null;

    /*---------------------*/
    /* Save the processor. */
    /*---------------------*/
    BusyScreen.setBusy(true);
    Services.saveProcessor($ctrl.pInfo)
      .then((id) => {
        $ctrl.pInfo.id = id;
        $scope.$close($ctrl.pInfo);
        Alert.success('Processor saved');
      })
      .catch((r) => {
        (BusyScreen.setBusy(false),
          Alert.error(r.parsedResponse.message(), 'Processor error', true));
      })
      .finally(() => BusyScreen.setBusy(false));
  };
}

/*****************************************************************************
 * $ctrl.testCredentials */
/**
 * @ngdoc     function
 * @name      testCredentials
 * @description
 * Sends API tokens to server for testing with payment processor.
 *****************************************************************************/
/* @ngInject */ function testCredentials($ctrl, Alert, BusyScreen, Services) {
  return function () {
    if (!$ctrl.valTokens.validate()) return;
    $ctrl.valTokens.reset();

    BusyScreen.setBusy(true);
    Services.testTokens(
      $ctrl.pInfo.type,
      !!$ctrl.pInfo.forPOS,
      $ctrl.pInfo.apiTokens
    )
      .then(() => {
        $ctrl.isUntested = false;
        Alert.success(
          'Your API information has been verified',
          'Credentials Verified'
        );
      })
      .catch((r) => {
        $ctrl.isUntested = true;
        Alert.error(r.statusText, 'Credentials Not Valid', true);
      })
      .finally(() => BusyScreen.setBusy(false));
  };
}

/*****************************************************************************
 * $ctrl.toggleTestAcct */
/**
 * @ngdoc     function
 * @name      toggleTestAcct
 * @description
 * Test account indicator click handler. Toggles `apiTokens.isTestAcct`.
 *****************************************************************************/
/* @ngInject */ function toggleTestAcct($ctrl) {
  return () =>
    ($ctrl.pInfo.apiTokens.isTestAcct = !$ctrl.pInfo.apiTokens.isTestAcct);
}

/*****************************************************************************
 * watchActive */
/**
 * @function watchActive
 * @returns  function  Deregistration function.
 * Adds a watch for active flag to set `$ctrl.isUntested` when flag changes.
 *****************************************************************************/
/* @ngInject */ function watchActive($ctrl, $scope) {
  var unwatch = $scope.$watch(
    '$ctrl.pInfo.active',
    (nv, ov) => {
      if (!angular.equals(nv, ov))
        unwatch(($ctrl.isUntested = !!$ctrl.pConfig.testTokens));
    },
    true
  );

  $scope.$on('destroy', unwatch);
  return unwatch;
}

/*****************************************************************************
 * watchInfo */
/**
 * @function watchInfo
 * @returns  function  Deregistration function.
 * Adds a watch for changes to processor info to set `$ctrl.isModified` when
 * info changes. Watcher is deregistered after info changes or when the scope
 * is destroyed.
 *****************************************************************************/
/* @ngInject */ function watchInfo($ctrl, $scope) {
  var unwatch = $scope.$watch(
    '$ctrl.pInfo',
    (nv, ov) => {
      if (!angular.equals(nv, ov)) unwatch(($ctrl.isModified = true));
    },
    true
  );

  $scope.$on('destroy', unwatch);

  return unwatch;
}

/*****************************************************************************
 * watchTokens */
/**
 * @function watchTokens
 * @returns  function  Deregistration function.
 * Adds a watch for changes to processor tokens to set `$ctrl.isUntested` when
 * tokens change. Watcher is deregistered after changes or when the scope is
 * destroyed.
 *****************************************************************************/
/* @ngInject */ function watchTokens($ctrl, $scope) {
  var unwatch = $scope.$watch(
    '$ctrl.pInfo.apiTokens',
    (nv, ov) => {
      if (!angular.equals(nv, ov))
        unwatch(($ctrl.isUntested = !!$ctrl.pConfig.testTokens));
    },
    true
  );

  $scope.$on('destroy', unwatch);
  return unwatch;
}

/*****************************************************************************
 * watchType */
/**
 * @function watchType
 * @returns  function  Deregistration function.
 * Adds a watch for change to processor type. Sets the processor configuration
 * and initializes processor info for a new processor. Watcher is deregistered
 * when the scope is destroyed.
 *****************************************************************************/
/* @ngInject */ function watchType($ctrl, $scope, SERVER) {
  /*-----------------------------------------*/
  /* Set processor config when type changes. */
  /*-----------------------------------------*/
  var unwatch = $scope.$watch('$ctrl.pInfo.type', (nv, ov) => {
    if (nv !== ov) {
      $ctrl.pConfig =
        SERVER.processorConfigs.find((c) => c.type === $ctrl.pInfo.type) || {};
      $ctrl.valTokens.reset();

      if (!$ctrl.isEdit) {
        $ctrl.pInfo.forPOS = $ctrl.pConfig.forPOS && !$ctrl.pConfig.forWS;
        $ctrl.pInfo.forWS = $ctrl.pConfig.forWS && !$ctrl.pConfig.forPOS;
      }
    }
  });

  $scope.$on('destroy', unwatch);

  return unwatch;
}